import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import { ChannelBadge } from "../ui/ChannelBadge";
import { FLOW_TEXT_INPUT_BASE } from "../../styles/twDesignPrimitives";

export type ListFilterCustomOption = {
  value: string;
  label: string;
  channel?: "email" | "whatsapp";
};

const BADGE_COLUMN_WIDTH = "5.75rem";
const LABEL_BADGE_GAP = "gap-3";

function OptionLabelWithBadge({
  label,
  channel,
  reserveBadgeSpace = false,
  className = "",
}: {
  label: string;
  channel?: "email" | "whatsapp";
  reserveBadgeSpace?: boolean;
  className?: string;
}) {
  if (!reserveBadgeSpace && !channel) {
    return (
      <span className={`block min-w-0 truncate text-left ${className}`}>{label}</span>
    );
  }

  return (
    <span
      className={`flex min-w-0 flex-1 items-center ${LABEL_BADGE_GAP} ${className}`}
    >
      <span className="min-w-0 flex-1 truncate text-left">{label}</span>
      <span
        className="flex shrink-0 justify-end"
        style={{ width: BADGE_COLUMN_WIDTH }}
      >
        {channel ? <ChannelBadge channel={channel} /> : null}
      </span>
    </span>
  );
}

type Props = {
  label: string;
  value: string;
  options: ReadonlyArray<ListFilterCustomOption>;
  onChange: (value: string) => void;
  className?: string;
};

export function ListFilterCustomSelect({
  label,
  value,
  options,
  onChange,
  className = "",
}: Props) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const selected = useMemo(
    () => options.find((option) => option.value === value) ?? options[0],
    [options, value]
  );

  const hasChannelOptions = useMemo(
    () => options.some((option) => Boolean(option.channel)),
    [options]
  );

  useEffect(() => {
    if (!open) return;

    function onDocMouseDown(event: MouseEvent) {
      const root = rootRef.current;
      if (!root || root.contains(event.target as Node)) return;
      setOpen(false);
    }

    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [open]);

  return (
    <div ref={rootRef} className={`relative min-w-[200px] shrink-0 text-left ${className}`}>
      <p className="mb-1 block w-full text-left text-xs font-medium text-slate-600">
        {label}
      </p>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        className={`${FLOW_TEXT_INPUT_BASE} flex items-center justify-between gap-2 !text-left !font-[inherit] ${
          open ? "border-[#3182CE] ring-2 ring-[#3182CE]/30" : ""
        }`}
      >
        <span className="min-w-0 flex-1 text-left">
          <OptionLabelWithBadge
            label={selected?.label ?? ""}
            channel={selected?.channel}
            reserveBadgeSpace={hasChannelOptions}
          />
        </span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-slate-500 transition ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      {open ? (
        <div
          className="absolute left-0 top-full z-50 mt-1 min-w-full w-max max-w-md overflow-hidden rounded-xl border border-slate-200 bg-white text-left shadow-lg"
          role="listbox"
        >
          <div className="max-h-48 overflow-y-auto py-0.5">
            {options.map((option) => {
              const active = option.value === value;
              return (
                <button
                  key={option.value || "all"}
                  type="button"
                  role="option"
                  aria-selected={active}
                  className={`flex w-full min-w-max items-center gap-2 px-3 py-2.5 text-left text-sm font-[inherit] hover:bg-slate-50 ${
                    active ? "bg-orange-50/80 text-slate-900" : "text-slate-800"
                  }`}
                  onClick={() => {
                    onChange(option.value);
                    setOpen(false);
                  }}
                >
                  <span className="flex w-4 shrink-0 justify-center">
                    {active ? (
                      <Check className="h-4 w-4 text-orange-600" />
                    ) : null}
                  </span>
                  <OptionLabelWithBadge
                    label={option.label}
                    channel={option.channel}
                    reserveBadgeSpace={hasChannelOptions}
                    className="min-w-[12rem]"
                  />
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}
